/**
 * 24-hour strip per hour tag: filled = tagged (in the tag's colour), grey =
 * not tagged, dashed = Unknown (the Drop Generator never acts on it).
 * For Media Devoted | Created By Ciro Autiero
 */

import { hourTagVerdict, type HourPerformanceTagsResponse } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { cellTitle } from './hour-cell';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const COLS = { gridTemplateColumns: 'minmax(7rem, 9rem) repeat(24, minmax(1.25rem, 1fr))' };

function Cell({ data, tag, hour }: { data: HourPerformanceTagsResponse; tag: string; hour: number }) {
  const v = hourTagVerdict(data, tag, hour);
  const color = data.tagColors?.[tag];
  return (
    <div
      title={cellTitle(tag, hour, v)}
      data-state={v.state}
      className={cn(
        'h-6 rounded-sm border',
        v.state === 'tagged' && 'border-transparent bg-primary',
        v.state === 'not_tagged' && 'border-transparent bg-muted',
        v.state === 'unknown' && 'border-dashed border-muted-foreground/40 bg-transparent',
      )}
      style={v.state === 'tagged' && color ? { backgroundColor: color } : undefined}
    />
  );
}

export function HourTagsGrid({ data }: { data: HourPerformanceTagsResponse }) {
  return (
    <div className="overflow-x-auto" data-testid="hour-tags-grid">
      <div className="grid min-w-[40rem] items-center gap-1" style={COLS}>
        <span className="text-xs text-muted-foreground">Tag</span>
        {HOURS.map((h) => (
          <span key={h} className="text-center text-[10px] tabular-nums text-muted-foreground">
            {String(h).padStart(2, '0')}
          </span>
        ))}
        {data.tagNames.map((tag) => (
          <div key={tag} className="contents">
            <span className="truncate pr-2 text-sm font-medium" title={tag}>
              {tag}
            </span>
            {HOURS.map((h) => (
              <Cell key={h} data={data} tag={tag} hour={h} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
